

import { mapGetters, mapActions } from 'vuex';

import { Geo } from '@sopheos/orbjs_geo';

// API
import * as apiContributions from '../api/private/contributions';
import * as apiGeonames from '../api/geonames/geo';
import * as apiGeo from '../api/public/geo';

// libraries
import { addMarker, getPoint, displayBound } from '../libraries/carto';
import { getShapeId, getPlaceNameWithPreposition } from '../libraries/utils';

// models
import Location from '../models/location';

import errorsMixin from '../mixins/error-handler';

export default {
  name: 'SDetailMap',
  mixins: [errorsMixin],
  props: {
    playground: {
      type: Object,
      required: true,
    },
    hasContribution: {
      type: Boolean,
      default: false,
    },
    contributions: {
      type: Object,
      default: null,
    },
    loadingContributions: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      toggleTooltip: false,
      error: null,
      dialogMap: false,
      dialogMapTitle: 'Carte',
      dialogMapLabel: 'Carte de localisation',
      dialogMapForm: false,
      newMapLat: null,
      newMapLon: null,
      mapFormTitle: 'Localiser',
      mapFormLabel: 'Contribution - Localiser',
      mapFormOptions: {
        contentClass: 'map-form-dialog',
      },
      mapFormButtons: {
        closeText: 'Annuler',
        confirmText: 'Valider',
      },
      mapFormLoading: false,
      waitEditLocation: false,
      contributionValue: null,
      mapToken: null,
      loadingMap: true,
      timeoutMapToken: null,
      mapErrorMessage: null,
      showDeleteContribution: false,
    };
  },
  computed: {
    ...mapGetters({
      isSmall: 'screen/isSmall',
      isMedium: 'screen/isMedium',
      isLarge: 'screen/isLarge',
      isAuth: 'authToken/isAuth',
      getMapToken: 'map/getMapToken',
      getMapTokenExp: 'map/getMapTokenExp',
      userSecured: 'preferences/userSecured',
      isHGte: 'screen/isHGte',
      isWGte: 'screen/isWGte',
    }),
    mapOptions() {
      return {
        style: `${process.env.VUE_APP_MAP_URL}styles/osm-bright/style.json`,
        center: [this.playground.location.lon, this.playground.location.lat],
        zoom: 13,
        minZoom: 12,
      };
    },
    editMapOptions() {
      return {
        style: `${process.env.VUE_APP_MAP_URL}styles/osm-bright/style.json`,
        center: [this.newMapLon, this.newMapLat],
        zoom: 13,
        minZoom: 12,
      };
    },
    urlLatLon() {
      const point = new Geo.Point(this.playground.location.lat, this.playground.location.lon);
      return `${point.getLatInt()}/${point.getLonInt()}`;
    },
    gridMapDetailClass() {
      const isMedium = this.isMedium ? 'is-medium' : 'is-not-medium';
      const isLarge = this.isLarge ? 'is-large' : 'is-not-large';
      return `${isMedium} ${isLarge}`;
    },
    placeNameWithPreposition() {
      return getPlaceNameWithPreposition(this.playground.place.name);
    },
  },
  watch: {
    isAuth(value) {
      if (!value) {
        this.dialogMapForm = false;
      }
    },
    hasContribution() {
      this.contributionValue = this.hasContribution;
    },
    loadingContributions() {
      if (!this.loadingContributions && this.userSecured && this.waitEditLocation) {
        // After logging in, call the API to get the contribution data
        if (this.contributionValue && this.contributions === null) {
          this.$emit('get-contributions');
          return;
        }
        this.setNewLocation();
        this.dialogMapForm = true;
        this.waitEditLocation = false;
      }
    },
    dialogMap() {
      if (this.dialogMap) {
        this.checkTokenMap();
      } else {
        this.resetTokenMap();
      }
    },
    dialogMapForm() {
      if (this.dialogMapForm) {
        this.checkTokenMap();
      } else {
        this.resetTokenMap();
      }
      this.$emit('has-unsave', this.dialogMapForm);
    },
  },
  mounted() {
    this.contributionValue = this.hasContribution;
  },
  beforeDestroy() {
    clearTimeout(this.timeoutMapToken);
  },
  methods: {
    ...mapActions({
      setMapToken: 'map/setMapToken',
    }),
    checkTokenMap() {
      if (!this.getMapToken || this.getMapTokenExp < Date.now()) {
        this.callMapToken();
      } else {
        this.mapToken = this.getMapToken;
        const timeLeft = this.getMapTokenExp - Date.now() - 5000;
        if (timeLeft <= 0) {
          this.callMapToken();
        } else {
          clearTimeout(this.timeoutMapToken);
          this.timeoutMapToken = setTimeout(() => {
            this.callMapToken();
          }, timeLeft);
        }
      }
    },
    callMapToken() {
      apiGeo.mapToken(this).then((result) => {
        this.mapToken = result.access_token;
        this.setMapToken({ token: result.access_token, exp: result.expires_in });

        const timeLeft = this.getMapTokenExp - Date.now();
        clearTimeout(this.timeoutMapToken);
        this.timeoutMapToken = setTimeout(() => {
          this.callMapToken();
        }, timeLeft);

      // Empty catch, error will be generated by mapbox if token is invalid
      }).catch(() => null);
    },
    resetTokenMap() {
      this.mapToken = null;
      this.loadingMap = true;
      clearTimeout(this.timeoutMapToken);
      this.timeoutMapToken = null;
      this.mapErrorMessage = null;
    },
    openRoute() {
      if (/(iPhone|iPod|iPad)/.test(navigator.platform)) {
        window.open(`maps://maps.google.com/maps?daddr=${this.playground.location.lat},${this.playground.location.lon}&amp;ll=`);
      } else {
        window.open(`https://maps.google.com/maps?daddr=${this.playground.location.lat},${this.playground.location.lon}&amp;ll=`);
      }
    },
    showMapForm() {
      if (this.userSecured) {
        // If they are not yet set, call the API to get the contribution data
        if (!(this.newMapLon && this.newMapLat) && this.contributionValue && this.contributions === null) {
          this.waitEditLocation = true;
          this.$emit('get-contributions');
          return;
        }
        this.setNewLocation();
        this.dialogMapForm = true;
      } else {
        this.$emit('login');
        this.waitEditLocation = true;
      }
    },
    closeMapForm() {
      this.dialogMapForm = false;
      this.waitEditLocation = false;
      this.error = null;
      this.errors$.reset();
      this.newMapLat = null;
      this.newMapLon = null;
    },
    setNewLocation() {
      if (this.contributions?.location?.lat && this.contributions?.location?.lon) {
        this.newMapLon = this.contributions.location.lon;
        this.newMapLat = this.contributions.location.lat;
      } else {
        this.newMapLon = this.playground.location.lon;
        this.newMapLat = this.playground.location.lat;
      }
    },
    submitForm() {
      const data = {
        playground: this.playground.id,
        lat: this.newMapLat,
        lon: this.newMapLon,
      };

      this.mapFormLoading = true;
      apiContributions.contribution(data, this).then(() => {
        this.closeMapForm();
        this.contributionValue = true;
        this.$emit('contribution', {
          lat: data.lat,
          lon: data.lon,
        });
      }).catch((error) => {
        // No change on this field. Just close the form
        if (this.errors$.getCode('contribution', 'form') === 'no_change') {
          this.closeMapForm();
          return;
        }

        // Validation error
        if (error.response && error.response.status === 400) {
          this.errors$.handleError(true, 'geo', 'position');
        }
      }).finally(() => {
        this.mapFormLoading = false;
      });
    },
    deleteContribution() {
      this.closeMapForm();
      this.contributionValue = false;
      this.$emit('delete-contribution');
    },
    mapClicked(map, e) {
      this.errors$.reset('geo');
      this.errors$.handleError(e.lngLat.lat < -90 || e.lngLat.lat > 90 || e.lngLat.lng < -180 || e.lngLat.lng > 180, 'geo', 'position');

      apiGeonames.reverse({ lat: e.lngLat.lat, lon: e.lngLat.lng }, null).then((res) => {
        const location = new Location(res.result);
        // Vérifie si les nouvelles coordonnées sont dans la même localité
        const newShapeId = getShapeId(location.city, location.sub);
        if (this.playground.place.shapeId === newShapeId) {
          this.error = null;
          this.newMapLon = e.lngLat.lng;
          this.newMapLat = e.lngLat.lat;
          map.getSource('main-point-src').setData(getPoint([e.lngLat.lng, e.lngLat.lat]));
        } else {
          this.error = `Le point doit se trouver dans la localité de ${this.playground.place.name}`;
        }
      }).catch((error) => {
        // Validation error
        if (error.response && error.response.status === 400) {
          this.errors$.handleError(true, 'geo', 'position');
        }
      });
    },
    mapLoaded(map, edit = false) {
      const lat = this.contributionValue && this.dialogMapForm ? this.newMapLat : this.playground.location.lat;
      const lng = this.contributionValue && this.dialogMapForm ? this.newMapLon : this.playground.location.lon;
      addMarker(map, {
        lat,
        lng,

        icon: {
          label: 'main-point',
          url: '/img/map/gmap.png',
          width: 42,
          height: 42,
          left: 0,
          top: 0.5,
        },
      });

      if (edit) {
        const params = this.playground.place.shapeIsSub ? { sub: this.playground.place.shapeId } : { city: this.playground.place.shapeId };
        apiGeonames.shape(params, null).then((res) => {
          displayBound(map, res.geojson.coordinates, res.geojson.type);
        }).catch((error) => {
          if (error.response && error.response.status === 400) {
            this.errors$.handleError(true, 'geo', 'form');
          }
        });
      }
    },
    mapError(map, e) {
      if (/^40[0-9]$/.test(e.error.status)) {
        this.mapErrorMessage = e.error.message;
        this.mapToken = null;
        this.loadingMap = false;
        clearTimeout(this.timeoutMapToken);
        this.setMapToken({ token: null, exp: null });
      }
    },
  },
};

